import { Text } from "@ttc3k/trekker";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderDeliveryMethodInput } from "gql/generated";
import { AddressSearchField } from "shared/components/organisms/SurveyQuestionInput/AddressSearchField";
import type { AddressSearchResponseValue } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import { formatPrice } from "shared/utils";
import { Flex, styled } from "styled-system/jsx";
import { useCartContext } from "widgets/booker/contexts";
import { CollapsableStep } from "widgets/booker/shared";
import { useDeliveryMethod } from "./useDeliveryMethod";

const ADDRESS_REPRICE_DELAY_MS = 400;

const toDeliveryAddress = (value: AddressSearchResponseValue) => ({
  line1: value.line1 || null,
  line2: value.line2 || null,
  city: value.city || null,
  subdivision: value.subdivision || null,
  postalCode: value.postalCode || null,
  country: value.country || null,
});

export const DeliveryMethod = () => {
  const { t } = useTranslation("booker", { keyPrefix: "DELIVERY" });
  const { customerAddress } = useCartContext();
  const {
    required,
    isComplete,
    pickupEnabled,
    pickupLocations,
    shippingEnabled,
    delivery,
    updateDelivery,
    shippingTotal,
    deliveryAvailable,
    hasCompleteAddress,
  } = useDeliveryMethod();

  const [address, setAddress] = useState<AddressSearchResponseValue | null>(
    null,
  );
  const repriceTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(repriceTimer.current), []);

  const method = delivery?.method ?? null;

  // Single-option storefronts preselect so the buyer has nothing to click.
  useEffect(() => {
    if (!required || delivery) return;
    if (pickupEnabled && !shippingEnabled && pickupLocations.length === 1) {
      updateDelivery({
        method: OrderDeliveryMethodInput.Pickup,
        pickupLocationId: pickupLocations[0]._id,
      });
    } else if (shippingEnabled && !pickupEnabled) {
      selectShip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required, pickupEnabled, shippingEnabled, pickupLocations.length]);

  if (!required) return null;

  const commitAddress = (value: AddressSearchResponseValue | null) => {
    setAddress(value);
    clearTimeout(repriceTimer.current);
    repriceTimer.current = setTimeout(() => {
      updateDelivery({
        method: OrderDeliveryMethodInput.Ship,
        shippingAddress: value ? toDeliveryAddress(value) : null,
      });
    }, ADDRESS_REPRICE_DELAY_MS);
  };

  function selectShip() {
    const initial = address ?? customerAddress ?? null;
    setAddress(initial);
    updateDelivery({
      method: OrderDeliveryMethodInput.Ship,
      shippingAddress: initial ? toDeliveryAddress(initial) : null,
    });
  }

  const selectPickup = () =>
    updateDelivery({
      method: OrderDeliveryMethodInput.Pickup,
      pickupLocationId:
        pickupLocations.length === 1 ? pickupLocations[0]._id : null,
    });

  return (
    <CollapsableStep title={t("TITLE")} complete={isComplete} stepId="delivery">
      {pickupEnabled && shippingEnabled && (
        <Flex gap="100" w="full">
          <ChoiceButton
            active={method === OrderDeliveryMethodInput.Ship}
            onClick={selectShip}
            label={t("SHIP")}
          />
          <ChoiceButton
            active={method === OrderDeliveryMethodInput.Pickup}
            onClick={selectPickup}
            label={t("PICKUP")}
          />
        </Flex>
      )}

      {method === OrderDeliveryMethodInput.Pickup && (
        <Flex flexDirection="column" gap="100">
          {pickupLocations.map(location => {
            const active = delivery?.pickupLocationId === location._id;
            return (
              <styled.button
                key={location._id}
                type="button"
                onClick={() =>
                  updateDelivery({
                    method: OrderDeliveryMethodInput.Pickup,
                    pickupLocationId: location._id,
                  })
                }
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="050"
                p="200"
                textAlign="left"
                borderRadius="100"
                border="2px solid"
                borderColor={active ? "border.dark" : "border.light"}
                bg={active ? "ink.100" : "white"}
                cursor="pointer"
                _hover={{ borderColor: "border.dark" }}
              >
                <Text visual="bodySemiBold" color="text.dark">
                  {location.name}
                </Text>
                {location.address && (
                  <Text visual="smallRegular" color="text.mid">
                    {location.address}
                  </Text>
                )}
                {active && location.instructions && (
                  <Text visual="smallRegular" color="text.light">
                    {location.instructions}
                  </Text>
                )}
              </styled.button>
            );
          })}
          <Text visual="smallRegular" color="text.mid">
            {t("PICKUP_FREE")}
          </Text>
        </Flex>
      )}

      {method === OrderDeliveryMethodInput.Ship && (
        <Flex flexDirection="column" gap="100">
          <AddressSearchField
            label={t("SHIPPING_ADDRESS")}
            value={address}
            onChange={commitAddress}
          />
          {deliveryAvailable === false ? (
            <Text visual="smallRegular" color="text.error.dark">
              {t("SHIPPING_UNAVAILABLE")}
            </Text>
          ) : hasCompleteAddress ? (
            <Text visual="smallRegular" color="text.mid">
              {shippingTotal > 0
                ? t("SHIPPING_RATE", { amount: `$${formatPrice(shippingTotal)}` })
                : t("SHIPPING_FREE")}
            </Text>
          ) : (
            address && (
              <Text visual="smallRegular" color="text.mid">
                {t("ADDRESS_INCOMPLETE")}
              </Text>
            )
          )}
        </Flex>
      )}
    </CollapsableStep>
  );
};

const ChoiceButton = ({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) => (
  <styled.button
    type="button"
    onClick={onClick}
    display="flex"
    flex="1"
    justifyContent="center"
    alignItems="center"
    py="150"
    borderRadius="100"
    border="2px solid"
    borderColor={active ? "border.dark" : "border.light"}
    bg={active ? "ink.100" : "white"}
    cursor="pointer"
    transition="all 0.15s ease"
    _hover={{ borderColor: "border.dark" }}
  >
    <Text visual="bodySemiBold" color={active ? "text.dark" : "text.mid"}>
      {label}
    </Text>
  </styled.button>
);
